import { useState, type ComponentProps } from 'react'
import {
  useExaminationRoomAppointments,
  useSettingAppointments,
} from '@/hooks/useAppointments'
import { useExaminationRoomAppointmentSetting } from '@/hooks/useAppointmentSetting'
import { useAuth } from '@/hooks/useAuth'
import { appointmentService, type CreateAppointmentData } from '@/services/appointment_service'
import { ExaminationRoom } from '@/pages/clinic/examinationRooms/columns'
import { format, parseISO, startOfDay, endOfDay } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { logger } from '@magnetar/utils'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Appointment } from '@/hooks/useAppointments'
import {
  CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Settings,
  Plus,
  RefreshCw,
  Search,
  X,
} from 'lucide-react'
import { AppointmentForm, type AppointmentFormSubmitData } from './AppointmentForm'

type BadgeVariant = ComponentProps<typeof Badge>['variant']

type AppointmentListProps = {
  examinationRoom: ExaminationRoom
  onSuccess?: () => void
}

const appointmentStatusOptions = [
  { value: 'SCHEDULED', label: '已排程' },
  { value: 'CHECKED_IN', label: '已報到' },
  { value: 'IN_PROGRESS', label: '看診中' },
  { value: 'COMPLETED', label: '已完成' },
  { value: 'CANCELLED', label: '已取消' },
  { value: 'NO_SHOW', label: '未出席' },
]

const appointmentSourceOptions = [
  { value: 'ONLINE', label: '線上預約' },
  { value: 'WALK_IN', label: '現場掛號' },
  { value: 'PHONE', label: '電話預約' },
]

export function AppointmentList({ examinationRoom, onSuccess }: AppointmentListProps) {
  const { token } = useAuth()
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [selectedDate, setSelectedDate] = useState<Date>(startOfDay(new Date()))
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  // 派發狀態
  const [isDispatchDialogOpen, setIsDispatchDialogOpen] = useState(false)
  const [dispatchTargetRoomId, setDispatchTargetRoomId] = useState<string>('')

  // 取得本診間的 setting，判斷模式
  const { appointmentSettings } = useExaminationRoomAppointmentSetting(
    examinationRoom.examination_room_id,
  )
  const currentSetting = appointmentSettings?.[0]
  const isSharedMode = currentSetting?.appointment_mode === 'SHARED_ROOMS'
  const sharedRooms = currentSetting?.examination_rooms ?? []

  // 依模式選擇撈法
  const dateRange = {
    startDate: format(selectedDate, "yyyy-MM-dd'T'HH:mm:ss'Z'"),
    endDate: format(endOfDay(selectedDate), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
  }
  const sharedQuery = useSettingAppointments({
    appointmentSettingId: isSharedMode ? currentSetting?.appointment_setting_id : undefined,
    page,
    pageSize,
    ...dateRange,
  })
  const roomQuery = useExaminationRoomAppointments({
    // 共用模式下這個查詢結果不會被使用；傳 0（falsy）會讓 hook 不帶診間過濾條件，
    // 與原本傳 undefined 產生完全相同的請求網址
    examinationRoomId: !isSharedMode ? examinationRoom.examination_room_id : 0,
    page,
    pageSize,
    ...dateRange,
  })
  const {
    appointments,
    pagination,
    isLoading,
    mutate: refreshAppointments,
  } = isSharedMode ? sharedQuery : roomQuery

  logger.log('appointments', appointments)

  // 過濾和搜索預約
  const filteredAppointments = appointments
    ? appointments.filter((appointment) => {
        // 狀態過濾
        if (statusFilter && appointment.appointment_status !== statusFilter) {
          return false
        }

        // 搜索過濾
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase()
          return (
            appointment.patient_name.toLowerCase().includes(searchLower) ||
            (appointment.patient_health_id &&
              appointment.patient_health_id.toLowerCase().includes(searchLower)) ||
            (appointment.patient_phone &&
              appointment.patient_phone.toLowerCase().includes(searchLower))
          )
        }

        return true
      })
    : []

  // 處理派發
  const handleDispatch = async () => {
    if (!token || !selectedAppointment || !dispatchTargetRoomId) return
    try {
      const response = await appointmentService.dispatchAppointment(
        selectedAppointment.appointment_id,
        Number(dispatchTargetRoomId),
        token,
      )
      if (response.success) {
        toast.success('派發成功')
        setIsDispatchDialogOpen(false)
        setDispatchTargetRoomId('')
        setSelectedAppointment(null)
        refreshAppointments()
      } else {
        toast.error('派發失敗', { description: response.error?.message })
      }
    } catch (err) {
      toast.error('派發失敗', {
        description: err instanceof Error ? err.message : undefined,
      })
    }
  }

  // 處理新增掛號(預約)
  const handleAddAppointment = async (data: AppointmentFormSubmitData) => {
    if (!token) return
    if (!currentSetting) {
      toast.error('找不到預約掛號設定，請先在「預約掛號設定」建立')
      return
    }

    try {
      const response = await appointmentService.createAppointment(
        {
          ...data,
          // SHARED 模式下 examinationRoomId 由表單決定（可能為 undefined = 待派發）
          // DEFAULT 模式下強制使用本診間
          examinationRoomId: isSharedMode
            ? data.examinationRoomId
            : examinationRoom.examination_room_id,
          appointmentSettingId: currentSetting.appointment_setting_id,
        } as CreateAppointmentData,
        token,
      )

      if (response.success) {
        toast.success('預約新增成功')
      } else {
        toast.error('預約新增失敗')
      }

      setIsAddDialogOpen(false)
      refreshAppointments()
      if (onSuccess) onSuccess()
    } catch {
      toast.error('預約新增失敗')
    }
  }

  // 處理編輯預約
  const handleEditAppointment = async (data: AppointmentFormSubmitData) => {
    if (!token || !selectedAppointment) return

    try {
      await appointmentService.updateAppointment(selectedAppointment.appointment_id, data, token)
      refreshAppointments()
      toast.success('預約更新成功')
      setIsEditDialogOpen(false)
      setSelectedAppointment(null)
      if (onSuccess) onSuccess()
    } catch {
      toast.error('預約更新失敗')
    }
  }

  // 處理刪除預約
  const handleDeleteAppointment = async () => {
    if (!token || !selectedAppointment) return

    try {
      await appointmentService.deleteAppointment(selectedAppointment.appointment_id, token)

      toast.success('預約刪除成功')

      setIsDeleteDialogOpen(false)
      setSelectedAppointment(null)
      refreshAppointments()
      if (onSuccess) onSuccess()
    } catch {
      toast.error('預約刪除失敗')
    }
  }

  // 處理日期選擇
  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(startOfDay(date))
      setIsCalendarOpen(false)
    }
  }

  // 處理分頁變更
  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  // 獲取狀態標籤顏色
  // 回傳值包含 Badge 元件未在型別中列出的自訂色彩（blue/green/yellow），故以 string 表示
  const getStatusBadgeVariant = (status: string): string => {
    switch (status) {
      case 'SCHEDULED':
        return 'default'
      case 'CHECKED_IN':
        return 'secondary'
      case 'IN_PROGRESS':
        return 'blue'
      case 'COMPLETED':
        return 'green'
      case 'CANCELLED':
        return 'destructive'
      case 'NO_SHOW':
        return 'yellow'
      default:
        return 'outline'
    }
  }

  // 獲取來源標籤顏色
  // 回傳值包含 Badge 元件未在型別中列出的自訂色彩（blue/green/purple），故以 string 表示
  const getSourceBadgeVariant = (source: string): string => {
    switch (source) {
      case 'ONLINE':
        return 'blue'
      case 'WALK_IN':
        return 'green'
      case 'PHONE':
        return 'purple'
      default:
        return 'outline'
    }
  }

  // 獲取狀態顯示文字
  const getStatusLabel = (status: string) => {
    const option = appointmentStatusOptions.find((opt) => opt.value === status)
    return option ? option.label : status
  }

  // 獲取來源顯示文字
  const getSourceLabel = (source: string) => {
    const option = appointmentSourceOptions.find((opt) => opt.value === source)
    return option ? option.label : source
  }

  return (
    <div className="space-y-4">
      {/* 工具列 */}
      <div className="flex flex-col md:flex-row gap-4 justify-between">
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          {/* 日期範圍選擇器 */}
          <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full sm:w-auto justify-start">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(selectedDate, 'yyyy/MM/dd', { locale: zhTW })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                defaultMonth={selectedDate}
                selected={selectedDate}
                onSelect={handleDateSelect}
                numberOfMonths={1}
                locale={zhTW}
              />
            </PopoverContent>
          </Popover>

          {/* 狀態過濾 */}
          <Select
            value={statusFilter || 'ALL'}
            onValueChange={(value) => setStatusFilter(value === 'ALL' ? undefined : value)}
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="所有狀態" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">所有狀態</SelectItem>
              {appointmentStatusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          {/* 搜索框 */}
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="搜尋病患姓名或健保卡號..."
              className="w-full sm:w-[250px] pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-9 w-9"
                onClick={() => setSearchTerm('')}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* 重新整理按鈕 */}
          <Button variant="outline" size="icon" onClick={() => refreshAppointments()}>
            <RefreshCw className="h-4 w-4" />
          </Button>

          {/* 新增掛號(預約)按鈕 */}
          <Button onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            新增掛號(預約)
          </Button>
        </div>
      </div>

      {/* 預約列表 */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>預約日期</TableHead>
              <TableHead>預約掛號時間/序號</TableHead>
              <TableHead>病患姓名</TableHead>
              <TableHead>健保卡號</TableHead>
              <TableHead>聯絡電話</TableHead>
              <TableHead>掛號來源</TableHead>
              <TableHead>狀態</TableHead>
              {isSharedMode && <TableHead>派發診間</TableHead>}
              <TableHead className="text-right">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={isSharedMode ? 9 : 8} className="h-24 text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    <span className="ml-2">載入中...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredAppointments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isSharedMode ? 9 : 8} className="h-24 text-center">
                  沒有找到預約記錄
                </TableCell>
              </TableRow>
            ) : (
              filteredAppointments.map((appointment) => (
                <TableRow key={appointment.appointment_id}>
                  <TableCell>
                    {format(parseISO(appointment.appointment_date), 'yyyy/MM/dd', { locale: zhTW })}
                  </TableCell>
                  <TableCell>
                    {appointment.appointment_type === 'TIME_BASED'
                      ? appointment.appointment_time
                        ? format(parseISO(appointment.appointment_time), 'HH:mm', { locale: zhTW })
                        : '-'
                      : `序號 ${appointment.sequence_number || '-'}`}
                  </TableCell>
                  <TableCell>{appointment.patient_name}</TableCell>
                  <TableCell>{appointment.patient_health_id || '-'}</TableCell>
                  <TableCell>{appointment.patient_phone || '-'}</TableCell>
                  <TableCell>
                    <Badge
                      variant={getSourceBadgeVariant(appointment.appointment_source) as BadgeVariant}
                    >
                      {getSourceLabel(appointment.appointment_source)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={getStatusBadgeVariant(appointment.appointment_status) as BadgeVariant}
                    >
                      {getStatusLabel(appointment.appointment_status)}
                    </Badge>
                  </TableCell>
                  {isSharedMode && (
                    <TableCell>
                      {appointment.examination_room ? (
                        <Badge variant="outline">{appointment.examination_room.name}</Badge>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Badge variant="yellow">尚未指定</Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedAppointment(appointment)
                              setDispatchTargetRoomId('')
                              setIsDispatchDialogOpen(true)
                            }}
                          >
                            指定診間
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  )}
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <Settings className="h-4 w-4" />
                          <span className="sr-only">開啟選單</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>預約操作</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {isSharedMode && (
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedAppointment(appointment)
                              setDispatchTargetRoomId(
                                appointment.examination_room?.examination_room_id
                                  ? String(appointment.examination_room.examination_room_id)
                                  : '',
                              )
                              setIsDispatchDialogOpen(true)
                            }}
                          >
                            {appointment.examination_room ? '更改診間' : '指定診間'}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedAppointment(appointment)
                            setIsEditDialogOpen(true)
                          }}
                        >
                          編輯預約
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedAppointment(appointment)
                            setIsDeleteDialogOpen(true)
                          }}
                          className="text-destructive focus:text-destructive"
                        >
                          刪除預約
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* 指定診間對話框 */}
      <Dialog open={isDispatchDialogOpen} onOpenChange={setIsDispatchDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>指定看診診間</DialogTitle>
            <DialogDescription>
              為 {selectedAppointment?.patient_name} 指定看診的診間
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={dispatchTargetRoomId} onValueChange={setDispatchTargetRoomId}>
              <SelectTrigger>
                <SelectValue placeholder="選擇診間" />
              </SelectTrigger>
              <SelectContent>
                {sharedRooms.map((r) => (
                  <SelectItem key={r.examination_room_id} value={String(r.examination_room_id)}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDispatchDialogOpen(false)
                setDispatchTargetRoomId('')
                setSelectedAppointment(null)
              }}
            >
              取消
            </Button>
            <Button onClick={handleDispatch} disabled={!dispatchTargetRoomId}>
              確定
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 分頁 */}
      {pagination && pagination.totalPages > 1 && (
        <Pagination className="mt-4">
          <PaginationContent>
            <PaginationItem>
              {page === 1 ? (
                <span className="flex h-9 items-center gap-1 pl-2.5 pr-2.5 opacity-50">
                  <ChevronLeft className="h-4 w-4" />
                  <span>Previous</span>
                </span>
              ) : (
                <PaginationPrevious onClick={() => handlePageChange(Math.max(1, page - 1))} />
              )}
            </PaginationItem>

            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((pageNum) => (
              <PaginationItem key={pageNum}>
                <PaginationLink
                  onClick={() => handlePageChange(pageNum)}
                  isActive={pageNum === page}
                >
                  {pageNum}
                </PaginationLink>
              </PaginationItem>
            ))}

            <PaginationItem>
              {page === pagination.totalPages ? (
                <span className="flex h-9 items-center gap-1 pl-2.5 pr-2.5 opacity-50">
                  <span>Next</span>
                  <ChevronRight className="h-4 w-4" />
                </span>
              ) : (
                <PaginationNext
                  onClick={() => handlePageChange(Math.min(pagination.totalPages, page + 1))}
                />
              )}
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      {/* 新增掛號(預約)對話框 */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>新增掛號(預約)</DialogTitle>
            <DialogDescription>
              為 {examinationRoom.name} 診間新增一個預約。請填寫以下資訊。
            </DialogDescription>
          </DialogHeader>

          <AppointmentForm
            examinationRoomId={examinationRoom.examination_room_id}
            onSubmit={handleAddAppointment}
            onCancel={() => setIsAddDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* 編輯預約對話框 */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>編輯預約</DialogTitle>
            <DialogDescription>
              編輯 {selectedAppointment?.patient_name} 的預約資訊。
            </DialogDescription>
          </DialogHeader>

          {selectedAppointment && (
            <AppointmentForm
              examinationRoomId={examinationRoom.examination_room_id}
              appointment={selectedAppointment}
              onSubmit={handleEditAppointment}
              onCancel={() => {
                setIsEditDialogOpen(false)
                setSelectedAppointment(null)
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* 刪除預約確認對話框 */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>確認刪除預約</DialogTitle>
            <DialogDescription>
              您確定要刪除 {selectedAppointment?.patient_name} 的預約嗎？此操作無法撤銷。
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false)
                setSelectedAppointment(null)
              }}
            >
              取消
            </Button>
            <Button variant="destructive" onClick={handleDeleteAppointment}>
              確認刪除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
